import { useEffect, useMemo, useState } from "react";
import { isAdminRole, isHigherThanDistrictAdmin, UserRole } from "../../app/userSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { clearSharedAlert, SharedAlert } from "../../app/siteSlice";
import AlertMessage from "../../components/AlertMessage";
import BroadcastAlerts from "../../components/BroadcastAlerts";
import { LoadingTextSkeleton } from "../../components/LoadingTextSkeleton";
import AdminHome from "./AdminHome";
import TeacherHome from "./TeacherHome";
import StudentHome from "./StudentHome";

export default function Home() {
	const dispatch = useAppDispatch();
	const {
		firstName,
		lastName,
		course,
		role,
		isTeachersWriterAccount,
		school,
		schools,
		district,
		districts,
	} = useAppSelector((state) => state.user);
	const sharedAlert = useAppSelector((state) => state.site.sharedAlert);

	const [alerts, setAlerts] = useState<Record<number, string>>();
	const [lastMessage, setLastMessage] = useState<SharedAlert>();

	const isAdmin = isAdminRole(role);

	useEffect(() => {
		if (sharedAlert) {
			setLastMessage(sharedAlert);
			dispatch(clearSharedAlert());
		}
	}, [sharedAlert]);

	const welcomeText = useMemo(() => {
		const hasMultipleSchools = (schools?.length ?? 0) > 1;
		const hasMultipleDistricts = (districts?.length ?? 0) > 1;

		const welcome = `Welcome, ${firstName} ${lastName}${isTeachersWriterAccount ? " (Student)" : ""}! `;

		let instructions = "Use the tabs at the top of the screen ";

		switch (role) {
			case UserRole.SchoolAdmin:
				instructions += `to view your school and update course information, manage users, formulate prompts, or view reports.`;
				break;
			case UserRole.DistrictAdmin:
				instructions += `to view your district, update schools and courses, manage users, formulate prompts, or view reports.`;
				break;
			case UserRole.Writer:
				instructions += `to write practice essays, review essays written by your peers, take lessons to improve your understanding of writing, or view your reports.`;
				break;
			case UserRole.Proctor:
				instructions += `to create writing assignments, review completed peer reviews, assign lessons, view class reports, and manage your course.`;
				break;
			default:
				instructions += `to view all districts and schools, manage users, create new or modify existing prompts, or view reports.`;
				break;
		}

		const pickerWord = !isAdmin
			? "course"
			: role === UserRole.SchoolAdmin && hasMultipleSchools
				? "school"
				: role === UserRole.DistrictAdmin && hasMultipleDistricts
					? "district"
					: "";

		const pickerText = pickerWord
			? ` To access other ${pickerWord}s, click the ${pickerWord} name on the right.`
			: "";

		return welcome + instructions + pickerText;
	}, [firstName, lastName, isTeachersWriterAccount, role, schools, districts, isAdmin]);

	const header = useMemo(() => {
		if (!isAdmin) {
			return course?.name;
		} else if (school && role === UserRole.SchoolAdmin) {
			return school.name;
		} else if (district && role === UserRole.DistrictAdmin) {
			return district.name;
		}
	}, [course, district, school, role]);

	return (
		<>
			<BroadcastAlerts className="w-full mb-8" alerts={alerts} />

			{lastMessage && (
				<AlertMessage
					className="mb-8"
					type={lastMessage.level}
					message={lastMessage.message}
					showIcon={lastMessage.showIcon}
					onClear={() => setLastMessage(undefined)}
				/>
			)}

			<section className="mb-8">
				<h1>
					{header || (role && isHigherThanDistrictAdmin(role)) ? (
						header
					) : (
						<LoadingTextSkeleton className="w-60 h-8" />
					)}
				</h1>
				<p className="mt-4">{welcomeText}</p>
			</section>

			{role == UserRole.Writer && <StudentHome setAlerts={setAlerts} />}
			{role == UserRole.Proctor && <TeacherHome setAlerts={setAlerts} />}
			{isAdmin && <AdminHome setAlerts={setAlerts} />}
		</>
	);
}
